// src/components/ui/table.tsx
"use client"

import * as React from "react"
import { cn } from "lib/utils"

// ==== Container ====
function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-hidden rounded-xl bg-surface border border-line"
    >
      <div className="overflow-x-auto no-scrollbar">
        <table
          data-slot="table"
          className={cn(
            "w-full caption-bottom text-ui text-content-secondary",
            className
          )}
          {...props}
        />
      </div>
    </div>
  )
}

// ==== Header ====
function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        "bg-surface-muted border-b border-line",
        className
      )}
      {...props}
    />
  )
}

// ==== Body ====
function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn(
        "[&_tr:last-child]:border-0",
        "[&_tr:last-child>td:first-child]:rounded-bl-xl",
        "[&_tr:last-child>td:last-child]:rounded-br-xl",
        className
      )}
      {...props}
    />
  )
}

// ==== Footer ====
function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-surface-muted border-t border-line font-medium [&>tr]:last:border-b-0",
        className
      )}
      {...props}
    />
  )
}

// ==== Row ====
function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "border-b border-line-subtle last:border-0 transition-colors",
        "hover:bg-surface-hover",
        "data-[state=selected]:bg-brand-subtle",
        className
      )}
      {...props}
    />
  )
}

// ==== Head Cell ====
function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "h-11 px-4 text-left align-middle text-2xs font-bold uppercase tracking-wider text-content-muted whitespace-nowrap",
        className
      )}
      {...props}
    />
  )
}

// ==== Cell ====
function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "px-4 py-3.5 align-middle text-ui text-content-secondary",
        className
      )}
      {...props}
    />
  )
}

// ==== Caption ====
function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn(
        "text-content-muted mt-4 text-ui",
        className
      )}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
